import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, DatePicker, Form, Input, Select } from 'antd';
import StandardTable from '../../../../../components/common/StandardTable';
import dayjs from 'dayjs';
import { fetchSqlDataWithQuery } from '../../../../../services/api';
import type { VwQaqcPipeReportVc } from '../../../../../types';
import Button from '../../../../../components/common/Button';

const { RangePicker } = DatePicker;

const QaqcPipeNdtReportsVc: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<VwQaqcPipeReportVc[]>([]);
  const [filteredRows, setFilteredRows] = useState<VwQaqcPipeReportVc[]>([]);

  const cleanResult = (s?: string | null) => (s ?? '')
    .replace(/^\s*["']+|["']+\s*$/g, '')
    .trim();

  const load = async () => {
    const order = ' order by report_date desc, report_id, weldingdata_id, weld_no_rev';
    const sql = `select * from public.vw_qaqc_pipe_report_vc${order}`;
    setLoading(true);
    try {
      const res: any = await fetchSqlDataWithQuery(sql);
      const data: VwQaqcPipeReportVc[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setRows(data);
      setFilteredRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const applyFilters = () => {
    const v = form.getFieldsValue();
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(v.date_range) ? v.date_range : [];
    const filtered = rows.filter(r => {
      if (v.report_id && String(r.report_id) !== String(v.report_id)) return false;
      if (v.service_class_name && String(r.service_class_name) !== String(v.service_class_name)) return false;
      if (v.code_no && String(r.code_no) !== String(v.code_no)) return false;
      if (v.result && cleanResult(r.result) !== String(v.result)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    setFilteredRows(filtered);
  };
  const onValuesChange = () => { applyFilters(); };

  // Cascading options
  const reportIdOptions = useMemo(() => {
    const v = form.getFieldsValue();
    const temp = { ...v, report_id: undefined };
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(temp.date_range) ? temp.date_range : [];
    const pool = rows.filter(r => {
      if (temp.service_class_name && String(r.service_class_name) !== String(temp.service_class_name)) return false;
      if (temp.code_no && String(r.code_no) !== String(temp.code_no)) return false;
      if (temp.result && cleanResult(r.result) !== String(temp.result)) return false;
      if (temp.report_id && !contains(r.report_id, temp.report_id)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    const set = new Set<string>();
    pool.forEach(r => { if (r.report_id) set.add(String(r.report_id)); });
    return Array.from(set).sort().map(vv => ({ value: vv, label: vv }));
  }, [rows, form]);

  const serviceOptions = useMemo(() => {
    const v = form.getFieldsValue();
    const temp = { ...v, service_class_name: undefined };
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(temp.date_range) ? temp.date_range : [];
    const pool = rows.filter(r => {
      if (temp.report_id && String(r.report_id) !== String(temp.report_id)) return false;
      if (temp.code_no && String(r.code_no) !== String(temp.code_no)) return false;
      if (temp.result && cleanResult(r.result) !== String(temp.result)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    const set = new Set<string>();
    pool.forEach(r => { if (r.service_class_name) set.add(String(r.service_class_name)); });
    return Array.from(set).sort().map(vv => ({ value: vv, label: vv }));
  }, [rows, form]);

  const codeOptions = useMemo(() => {
    const v = form.getFieldsValue();
    const temp = { ...v, code_no: undefined };
    const contains = (a?: string, b?: string) => (a ?? '').toString().toLowerCase().includes((b ?? '').toString().toLowerCase());
    const inRange = (d?: string | null, from?: dayjs.Dayjs, to?: dayjs.Dayjs) => {
      if (!d || !from || !to) return true;
      const x = dayjs(d);
      return x.isValid() && (x.isSame(from, 'day') || x.isAfter(from, 'day')) && (x.isSame(to, 'day') || x.isBefore(to, 'day'));
    };
    const [d1, d2] = Array.isArray(temp.date_range) ? temp.date_range : [];
    const pool = rows.filter(r => {
      if (temp.report_id && String(r.report_id) !== String(temp.report_id)) return false;
      if (temp.service_class_name && String(r.service_class_name) !== String(temp.service_class_name)) return false;
      if (temp.result && cleanResult(r.result) !== String(temp.result)) return false;
      if (d1 && d2 && !inRange(r.report_date as any, d1, d2)) return false;
      return true;
    });
    const set = new Set<string>();
    pool.forEach(r => { if (r.code_no) set.add(String(r.code_no)); });
    return Array.from(set).sort().map(vv => ({ value: vv, label: vv }));
  }, [rows, form]);

  const columns: any[] = useMemo(() => ([
    { title: 'Date', dataIndex: 'report_date', key: 'report_date', width: 110 },
    { title: 'Report ID', dataIndex: 'report_id', key: 'report_id', width: 140 },
    { title: 'Weld ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 220 },
    { title: 'Rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 60 },
    { title: 'Result', dataIndex: 'result', key: 'result', width: 90, render: (v: any) => cleanResult(v) },
    { title: 'Size', dataIndex: 'pipe_size', key: 'pipe_size', width: 70 },
    { title: 'SC', dataIndex: 'service_class_name', key: 'service_class_name', width: 70 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 70 },
    { title: 'Type', dataIndex: 'weldtype_name', key: 'weldtype_name', width: 70 },
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 80 },
    { title: 'F/S', dataIndex: 'weld_category', key: 'weld_category', width: 60 },
    { title: 'Code', dataIndex: 'code_no', key: 'code_no', width: 65 },
    // VC checks (condensed titles)
    { title: 'W.Size', dataIndex: 'weld_size_ok', key: 'weld_size_ok', width: 70, render: (v:any)=> v? 'Yes':'No' },
    { title: 'Reinf.', dataIndex: 'reinforcement_ok', key: 'reinforcement_ok', width: 70, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Contour', dataIndex: 'contour_ok', key: 'contour_ok', width: 70, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Under/Overs', dataIndex: 'undersize_oversize_ok', key: 'undersize_oversize_ok', width: 90, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Cracks', dataIndex: 'no_cracks', key: 'no_cracks', width: 70, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Poros.', dataIndex: 'no_porosity', key: 'no_porosity', width: 70, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Undercut', dataIndex: 'undercut_ok', key: 'undercut_ok', width: 80, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Overlap', dataIndex: 'no_overlap', key: 'no_overlap', width: 80, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Fusion', dataIndex: 'fusion_fill_ok', key: 'fusion_fill_ok', width: 70, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Root Cond.', dataIndex: 'root_condition_ok', key: 'root_condition_ok', width: 90, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Penetr.', dataIndex: 'penetration_ok', key: 'penetration_ok', width: 80, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Root Reinf.', dataIndex: 'root_reinforcement_ok', key: 'root_reinforcement_ok', width: 95, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Slag/Spt', dataIndex: 'slag_spatter_removed', key: 'slag_spatter_removed', width: 85, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Inclu.', dataIndex: 'no_foreign_inclusions', key: 'no_foreign_inclusions', width: 75, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Toes clean', dataIndex: 'toes_adjacent_clean', key: 'toes_adjacent_clean', width: 95, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Discolor.', dataIndex: 'discoloration_ok', key: 'discoloration_ok', width: 85, render:(v:any)=> v? 'Yes':'No' },
    { title: 'HAZ crack', dataIndex: 'no_haz_cracking', key: 'no_haz_cracking', width: 90, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Coating chk', dataIndex: 'coating_damage_checked', key: 'coating_damage_checked', width: 100, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Misalign.', dataIndex: 'misalignment_ok', key: 'misalignment_ok', width: 85, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Twist/Bow', dataIndex: 'no_twist_bow', key: 'no_twist_bow', width: 90, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Dimen.', dataIndex: 'dimensional_checks_ok', key: 'dimensional_checks_ok', width: 85, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Smooth tack', dataIndex: 'smooth_transition_tack_ok', key: 'smooth_transition_tack_ok', width: 105, render:(v:any)=> v? 'Yes':'No' },
    { title: 'Spatter', dataIndex: 'spatter_ok', key: 'spatter_ok', width: 75, render:(v:any)=> v? 'Yes':'No' },

    { title: 'FLW Welder', dataIndex: 'flw_welder', key: 'flw_welder', width: 130 },
    { title: 'FW Welder', dataIndex: 'fw_welder', key: 'fw_welder', width: 130 },
    { title: 'FLW Date', dataIndex: 'flw_date', key: 'flw_date', width: 100 },
    { title: 'FW Date', dataIndex: 'fw_date', key: 'fw_date', width: 100 },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
  ]), []);

  return (
    <Card
      title="NDT Reports / Visual Check"
      extra={<Button onClick={() => { form.resetFields(); setFilteredRows(rows); }}>Reset</Button>}
      headStyle={{ marginBottom: 4, padding: '8px 12px' }}
      bodyStyle={{ padding: 8, paddingTop: 0 }}
      style={{ maxWidth: '100%', margin: '0 auto' }}
    >
      <Form
        form={form}
        layout="inline"
        initialValues={{ result: undefined, service_class_name: undefined, code_no: undefined, date_range: undefined }}
        onValuesChange={onValuesChange}
        style={{ marginBottom: 2, gap: 1, rowGap: 4 }}
      >
        <Form.Item name="date_range" style={{ marginRight: 6 }}>
          <RangePicker allowEmpty={[true, true]} placeholder={["From date", "To date"]} style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="report_id" style={{ marginRight: 6 }}>
          <Select allowClear showSearch placeholder="Report ID" style={{ width: 200 }} options={reportIdOptions}
                  filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="service_class_name" style={{ marginRight: 6 }}>
          <Select allowClear showSearch placeholder="S.Class" style={{ width: 100 }} options={serviceOptions}
                  filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="code_no" style={{ marginRight: 6 }}>
          <Select allowClear showSearch placeholder="Code" style={{ width: 80 }} options={codeOptions}
                  filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())} />
        </Form.Item>
        <Form.Item name="result" style={{ marginRight: 6 }}>
          <Select allowClear placeholder="Result" style={{ width: 100 }} options={[
            { value: 'PASS', label: 'PASS' },
            { value: 'FAIL', label: 'FAIL' },
            { value: 'REWORK', label: 'REWORK' },
          ]} />
        </Form.Item>
      </Form>

      <StandardTable
        loading={loading}
        rowKey={(r: VwQaqcPipeReportVc) => `${r.report_id}-${r.weldingdata_id}-${String(r.weld_no_rev ?? '')}`}
        dataSource={filteredRows}
        columns={columns}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
        bordered
      />
    </Card>
  );
};

export default QaqcPipeNdtReportsVc;
